import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Mail,
  MapPin,
  Send,
  MessageSquare,
  Building2,
  Headphones,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { ContactBackground } from '@/components/ui/ContactBackground';

const contactReasonIcons = [Building2, Headphones, FileText, MessageSquare, Mail];

const officeLocations = [
  {
    city: 'San Francisco',
    address: '548 Market St, Suite 35435',
    country: 'United States',
  },
  {
    city: 'London',
    address: '71-75 Shelton Street',
    country: 'United Kingdom',
  },
  {
    city: 'Istanbul',
    address: 'Şanlıurfa Caddesi, Sultanbeyli',
    country: 'Turkey',
  },
];

export function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    reason: 'sales',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contactReasons = [
    { value: 'sales', label: t('contact.reasons.sales'), icon: Building2 },
    { value: 'support', label: t('contact.reasons.support'), icon: Headphones },
    { value: 'enterprise', label: t('contact.reasons.enterprise'), icon: FileText },
    { value: 'partnership', label: t('contact.reasons.partnership'), icon: MessageSquare },
    { value: 'other', label: t('contact.reasons.other'), icon: Mail },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact.form.fillRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        reason: 'sales',
        message: '',
      });
    } catch {
      setError(t('contact.form.sendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <>
      <SEO {...SEO_CONFIG.contact} />
      <div>
      {/* Hero */}
      <section className="section-lg relative overflow-hidden min-h-[500px]">
        <ContactBackground />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-xl mb-6">
              {t('contact.title')} <span className="gradient-text">{t('contact.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-text-secondary">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="card p-8">
              <h2 className="heading-md mb-6">{t('contact.form.title')}</h2>

              {success ? (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto mb-4 text-success" size={64} />
                  <h3 className="heading-sm mb-2">{t('contact.form.successTitle')}</h3>
                  <p className="text-text-secondary mb-6">
                    {t('contact.form.successMessage')}
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="btn btn-secondary"
                  >
                    {t('contact.form.sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 rounded-lg bg-error/10 border border-error/20 flex items-start gap-3">
                      <AlertCircle className="text-error flex-shrink-0 mt-0.5" size={18} />
                      <p className="text-sm text-error">{error}</p>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('contact.form.name')} <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.form.namePlaceholder')}
                        className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-text-primary placeholder:text-text-muted"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('contact.form.email')} <span className="text-error">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.form.emailPlaceholder')}
                        className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-text-primary placeholder:text-text-muted"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('contact.form.company')}
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={t('contact.form.companyPlaceholder')}
                        className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-text-primary placeholder:text-text-muted"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        {t('contact.form.reason')}
                      </label>
                      <select
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        className="w-full h-[50px] px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-text-primary appearance-none cursor-pointer"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                      >
                        {contactReasons.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t('contact.form.message')} <span className="text-error">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder={t('contact.form.messagePlaceholder')}
                      className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all text-text-primary placeholder:text-text-muted resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all',
                      'bg-accent hover:bg-accent-hover text-white',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Send size={18} />
                    )}
                    {loading ? t('contact.form.sending') : t('contact.form.send')}
                  </button>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="space-y-8">
              <div>
                <h2 className="heading-md mb-6">{t('contact.info.title')}</h2>
                <div className="space-y-4">
                  <a
                    href="mailto:hello@ochtopus.com"
                    className="flex items-center gap-4 p-4 rounded-lg border border-border-default hover:border-accent transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Mail className="text-accent" size={24} />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{t('contact.info.general')}</p>
                      <p className="text-text-secondary">hello@ochtopus.com</p>
                    </div>
                  </a>

                  <a
                    href="mailto:support@ochtopus.com"
                    className="flex items-center gap-4 p-4 rounded-lg border border-border-default hover:border-accent transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <Headphones className="text-accent" size={24} />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{t('contact.info.support')}</p>
                      <p className="text-text-secondary">support@ochtopus.com</p>
                    </div>
                  </a>
                </div>
              </div>

              <div>
                <h3 className="heading-sm mb-4">{t('contact.offices.title')}</h3>
                <div className="space-y-4">
                  {officeLocations.map((office) => (
                    <div
                      key={office.city}
                      className="flex items-start gap-4 p-4 rounded-lg border border-border-default"
                    >
                      <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center">
                        <MapPin className="text-text-secondary" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">{office.city}</p>
                        <p className="text-text-secondary text-sm">{office.address}</p>
                        <p className="text-text-tertiary text-sm">{office.country}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card card-highlight p-6">
                <h3 className="font-semibold mb-2">{t('contact.support.title')}</h3>
                <p className="text-text-secondary text-sm mb-4">
                  {t('contact.support.description')}
                </p>
                <div className="flex gap-3">
                  <Link to="/docs" className="btn btn-secondary btn-sm flex-1">
                    {t('contact.support.docs')}
                  </Link>
                  <a
                    href="https://github.com/ochtopus/ochtopus/discussions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm flex-1"
                  >
                    {t('contact.support.community')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="section bg-bg-surface">
        <div className="container-sm text-center">
          <h2 className="heading-lg mb-4">{t('contact.faq.title')}</h2>
          <p className="text-text-secondary mb-6">
            {t('contact.faq.description')}
          </p>
          <Link to="/enterprise#faq" className="btn btn-primary">
            {t('contact.faq.viewFaq')}
          </Link>
        </div>
      </section>
    </div>
    </>
  );
}
